// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/Entregas.tsx
//  Página de Gerenciamento de Entregas. Busca, status, período, rota, motorista,
//  ordenação e KPIs rodam NO SERVIDOR; os filtros ficam na URL.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import {
  Package, Truck, CheckCircle2, Clock, XCircle,
  MapPin, PackageCheck, ExternalLink, Loader2,
} from 'lucide-react';
import { useEntrega } from '@/features/operacional/hooks/useEntrega';
import { calcularStatusEntrega } from '@/features/operacional/types';
import type { EntregaResumo, StatusEntrega } from '@/features/operacional/types';
import { useRotaCombo } from '@/features/rotas/hooks/useRotas';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { BarraFiltros } from '@/components/BarraFiltros';
import { BotaoExportar } from '@/components/BotaoExportar';
import { FiltroPeriodo } from '@/components/FiltroPeriodo';
import { PaginacaoRodape } from '@/components/PaginacaoRodape';
import { SortableTableHead } from '@/components/SortableTableHead';
import { PessoaLookupCombobox } from '@/components/PessoaLookupCombobox';
import { useFiltrosNaUrl } from '@/hooks/useFiltrosNaUrl';
import { usePaginacaoServidor } from '@/hooks/usePaginacaoServidor';
import { alternarOrdenacao, type DirecaoOrdenacao } from '@/lib/consultas';
import { EntregaDetalheDialog } from './EntregaDetalheDialog';

// ── Helpers ───────────────────────────────────────────────────────────────

function buildMapsUrl(e: { endereco?: string | null; numero?: string | null; bairro?: string | null; cidade?: string | null; uf?: string | null }) {
  const parts = [e.endereco, e.numero, e.bairro, e.cidade, e.uf].filter(Boolean).join(', ');
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts)}`;
}

// ── Helpers de status ─────────────────────────────────────────────────────

const statusConfig: Record<StatusEntrega, { label: string; className: string; icon: typeof Clock }> = {
  pendente:  { label: 'Pendente',    className: 'bg-blue-500/10 text-blue-500',     icon: Clock        },
  em_rota:   { label: 'Em Rota',     className: 'bg-yellow-500/10 text-yellow-500', icon: Truck        },
  entregue:  { label: 'Entregue',    className: 'bg-green-500/10 text-green-500',   icon: PackageCheck },
  cancelada: { label: 'Cancelada',   className: 'bg-red-500/10 text-red-500',       icon: XCircle      },
  retirada:  { label: 'Ret. na Loja', className: 'bg-purple-500/10 text-purple-500', icon: CheckCircle2 },
};

const BRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const NENHUMA = '__none__';

type Campo = 'numero' | 'cliente' | 'motorista' | 'movimento' | 'entrega' | 'valor';

const PADROES = {
  busca: '',
  status: 'todos',
  dtInicio: '',
  dtFim: '',
  rotaId: 0,
  motoristaId: 0,
  ordenarPor: 'entrega',
  direcao: 'desc',
  pagina: 1,
  tamanhoPagina: 20,
};

// ── Componente de ação de status ──────────────────────────────────────────

interface StatusActionDialogProps {
  entrega: EntregaResumo | null;
  onClose: () => void;
  onConfirm: (request: { isAberta?: boolean; isFinalizado?: boolean; isCancelada?: boolean }) => void;
  isUpdating: boolean;
}

function StatusActionDialog({ entrega, onClose, onConfirm, isUpdating }: StatusActionDialogProps) {
  if (!entrega) return null;
  const status = calcularStatusEntrega(entrega);

  const acoes: Array<{ label: string; className?: string; request: Parameters<typeof onConfirm>[0] }> = [];

  if (status === 'pendente') {
    acoes.push({ label: 'Marcar como Em Rota', request: { isAberta: false } });
    acoes.push({ label: 'Cancelar Entrega', className: 'text-destructive', request: { isCancelada: true } });
  }
  if (status === 'em_rota') {
    acoes.push({ label: 'Confirmar Entrega', request: { isFinalizado: true } });
    acoes.push({ label: 'Cancelar Entrega', className: 'text-destructive', request: { isCancelada: true } });
  }

  return (
    <Dialog open={!!entrega} onOpenChange={() => onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Atualizar Status — Entrega nº {entrega.numeroEntrega}
          </DialogTitle>
        </DialogHeader>
        <div className="py-2 text-sm text-muted-foreground">
          <p><strong>Cliente:</strong> {entrega.nomeRazao ?? '—'}</p>
          <p><strong>Data Entrega:</strong> {new Date(entrega.dtEntrega).toLocaleDateString('pt-BR')}</p>
          {entrega.endereco && (
            <p className="flex items-center gap-2 flex-wrap">
              <strong>Endereço:</strong>
              <span>{entrega.endereco}, {entrega.numero} — {entrega.bairro}, {entrega.cidade}/{entrega.uf}</span>
              <a
                href={buildMapsUrl(entrega)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-primary hover:underline text-xs"
              >
                <ExternalLink className="h-3 w-3" />Ver no Maps
              </a>
            </p>
          )}
        </div>
        {acoes.length === 0 ? (
          <p className="text-muted-foreground text-sm py-2">
            Nenhuma ação disponível para o status atual.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {acoes.map((acao) => (
              <Button
                key={acao.label}
                variant="outline"
                className={acao.className}
                disabled={isUpdating}
                onClick={() => onConfirm(acao.request)}
              >
                {acao.label}
              </Button>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Componente principal ──────────────────────────────────────────────────

const Entregas = () => {
  const [actionEntrega, setActionEntrega] = useState<EntregaResumo | null>(null);
  const [detalheId,     setDetalheId]     = useState<number | null>(null);
  const { rotas } = useRotaCombo();

  const { filtros, setFiltros, limparFiltros, filtrosAtivos } = useFiltrosNaUrl(PADROES);
  const pag = usePaginacaoServidor({
    controlado: { pagina: filtros.pagina, tamanhoPagina: filtros.tamanhoPagina, onChange: setFiltros },
  });

  const { entregas, total, totalPaginas, isLoading, isFetching, atualizarStatus, isUpdating, resumo } =
    useEntrega({
      pagina: pag.pagina,
      tamanhoPagina: pag.tamanhoPagina,
      filtros: {
        busca: filtros.busca, status: filtros.status, dtInicio: filtros.dtInicio, dtFim: filtros.dtFim,
        rotaId: filtros.rotaId, motoristaId: filtros.motoristaId,
        ordenarPor: filtros.ordenarPor, direcao: filtros.direcao as DirecaoOrdenacao,
      },
    });

  const ordenacao = { ordenarPor: filtros.ordenarPor, direcao: filtros.direcao as DirecaoOrdenacao };
  const onOrdenar = (campo: Campo) =>
    setFiltros(alternarOrdenacao(ordenacao, campo, campo === 'cliente' || campo === 'motorista' ? 'asc' : 'desc'));
  const cabecalho = { ...ordenacao, onOrdenar };
  const kpi = (v?: number) => (resumo ? v ?? 0 : '…');

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="p-6 space-y-6">
      {/* Cabeçalho */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Gerenciamento de Entregas</h1>
          <p className="text-muted-foreground mt-1">Acompanhe e atualize o status das entregas</p>
        </div>
      </div>

      {/* KPIs — calculados no servidor sobre o filtro (todas as páginas) */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Clock className="h-4 w-4" />Pendentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-500">{kpi(resumo?.pendentes)}</div>
            <p className="text-xs text-muted-foreground mt-1">Aguardando saída</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Truck className="h-4 w-4" />Em Rota
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-500">{kpi(resumo?.emRota)}</div>
            <p className="text-xs text-muted-foreground mt-1">Saíram para entrega</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <PackageCheck className="h-4 w-4" />Entregues
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{kpi(resumo?.entregues)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Concluídas{resumo && resumo.retiradas > 0 ? ` · ${resumo.retiradas} retirada(s) na loja` : ''}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Package className="h-4 w-4" />Valor Total
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-foreground">{BRL(resumo?.valorTotal ?? 0)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {kpi(resumo?.total)} {filtrosAtivos > 0 ? 'entregas no filtro' : 'entregas'} · sem canceladas
            </p>
          </CardContent>
        </Card>
      </div>

      <BarraFiltros
        busca={filtros.busca}
        onBuscar={(busca) => setFiltros({ busca })}
        placeholderBusca="Nº, cliente, motorista ou bairro..."
        filtrosAtivos={filtrosAtivos}
        onLimpar={limparFiltros}
        acoes={<BotaoExportar endpoint="/api/entregas/exportar" params={filtros} />}
      >
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Status</Label>
          <Select value={filtros.status} onValueChange={(status) => setFiltros({ status })}>
            <SelectTrigger className="h-9 w-[150px]" aria-label="Status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              <SelectItem value="pendente">Pendente</SelectItem>
              <SelectItem value="em_rota">Em Rota</SelectItem>
              <SelectItem value="entregue">Entregue</SelectItem>
              <SelectItem value="cancelada">Cancelada</SelectItem>
              <SelectItem value="retirada">Ret. na Loja</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <FiltroPeriodo
          rotulo="Movimento"
          inicio={filtros.dtInicio}
          fim={filtros.dtFim}
          onChange={({ inicio, fim }) => setFiltros({ dtInicio: inicio, dtFim: fim })}
        />
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Rota</Label>
          <Select
            value={filtros.rotaId ? String(filtros.rotaId) : NENHUMA}
            onValueChange={(v) => setFiltros({ rotaId: v === NENHUMA ? 0 : Number(v) })}
          >
            <SelectTrigger className="h-9 w-[180px]" aria-label="Rota"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NENHUMA}>Todas as rotas</SelectItem>
              {rotas.map((r) => (
                <SelectItem key={r.id} value={String(r.id)}>{r.descricao}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1 w-56">
          <Label className="text-xs text-muted-foreground">Motorista</Label>
          <PessoaLookupCombobox
            papel="motorista"
            value={filtros.motoristaId || null}
            onChange={(id) => setFiltros({ motoristaId: id ?? 0 })}
            placeholder="Todos os motoristas"
            className="h-9"
          />
        </div>
        {isFetching && !isLoading && (
          <Loader2 className="h-4 w-4 mb-2.5 animate-spin text-muted-foreground" aria-label="Atualizando" />
        )}
      </BarraFiltros>

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead campo="numero" {...cabecalho} className="w-28">Nº Entrega</SortableTableHead>
                  <SortableTableHead campo="cliente" {...cabecalho}>Cliente</SortableTableHead>
                  <SortableTableHead campo="movimento" {...cabecalho}>Movimento</SortableTableHead>
                  <SortableTableHead campo="entrega" {...cabecalho}>Dt. Entrega</SortableTableHead>
                  <TableHead>Endereço</TableHead>
                  <SortableTableHead campo="motorista" {...cabecalho}>Motorista</SortableTableHead>
                  <SortableTableHead campo="valor" {...cabecalho} alinharDireita>Valor Carga</SortableTableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      Carregando...
                    </TableCell>
                  </TableRow>
                ) : entregas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      {filtros.dtInicio || filtros.dtFim
                        ? 'Nenhuma entrega neste período — amplie o período para ver as anteriores'
                        : 'Nenhuma entrega encontrada'}
                    </TableCell>
                  </TableRow>
                ) : (
                  entregas.map((e: EntregaResumo) => {
                    const status = calcularStatusEntrega(e);
                    const cfg    = statusConfig[status];
                    const Icon   = cfg.icon;
                    const podeAtualizar = status === 'pendente' || status === 'em_rota';
                    return (
                      <TableRow key={e.id}>
                        <TableCell className="font-medium">{e.numeroEntrega}</TableCell>
                        <TableCell>
                          {e.nomeRazao || <span className="text-muted-foreground">Consumidor final</span>}
                        </TableCell>
                        <TableCell>{new Date(e.dtMovimento).toLocaleDateString('pt-BR')}</TableCell>
                        <TableCell>
                          {new Date(e.dtEntrega).toLocaleDateString('pt-BR')}
                          {e.horaEntrega && ` ${e.horaEntrega}`}
                        </TableCell>
                        <TableCell className="max-w-[180px] truncate">
                          {e.isRetirarLoja ? (
                            <span className="text-purple-600 flex items-center gap-1">
                              <MapPin className="h-3 w-3" />Retirada na loja
                            </span>
                          ) : e.endereco ? (
                            `${e.endereco}, ${e.numero ?? ''} — ${e.bairro ?? ''}, ${e.cidade ?? ''}/${e.uf ?? ''}`
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {e.motorista || <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="text-right font-medium">{BRL(e.valorTotalCarga ?? 0)}</TableCell>
                        <TableCell>
                          <Badge className={cfg.className}>
                            <span className="flex items-center gap-1">
                              <Icon className="h-3 w-3" />
                              {cfg.label}
                            </span>
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-1">
                            {!e.isRetirarLoja && e.endereco && (
                              <Button
                                size="sm" variant="ghost"
                                title="Ver no Google Maps"
                                asChild
                              >
                                <a href={buildMapsUrl(e)} target="_blank" rel="noopener noreferrer">
                                  <MapPin className="h-4 w-4 text-primary" />
                                </a>
                              </Button>
                            )}
                            <Button
                              size="sm" variant="ghost"
                              title="Ver detalhes"
                              onClick={() => setDetalheId(e.id)}
                            >
                              Detalhes
                            </Button>
                            {podeAtualizar && (
                              <Button
                                size="sm" variant="outline"
                                onClick={() => setActionEntrega(e)}
                              >
                                Atualizar
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <PaginacaoRodape {...pag.rodape({ total, totalPaginas })} />

      {/* Dialog de atualização de status */}
      <StatusActionDialog
        entrega={actionEntrega}
        onClose={() => setActionEntrega(null)}
        isUpdating={isUpdating}
        onConfirm={(request) => {
          if (actionEntrega) {
            atualizarStatus(
              { id: actionEntrega.id, data: request },
              { onSuccess: () => setActionEntrega(null) }
            );
          }
        }}
      />

      {/* Dialog de detalhe da entrega */}
      <EntregaDetalheDialog
        entregaId={detalheId}
        onClose={() => setDetalheId(null)}
      />
    </div>
  );
};

export default Entregas;
